import { getBrowser, areCookiesEnabled, setCookieValue, getCookieValue } from "./Utils/Browser";

export interface ModuleFetchDone {
    (resouce: any): void;
}

export interface LanguageSources {
    importEnglishLanguage: () => PromiseLike<{}>;
    importGermanLanguage: () => PromiseLike<{}>;
}

export interface LanguageModule {
    name: string;
    fetchResource(language: string, done: ModuleFetchDone): any;
}

interface I18nextPseudoOptions {
    enabled: boolean;
}

enum LargerLetters {
    a = 'α',
    b = 'ḅ',
    c = 'ͼ',
    d = 'ḍ',
    e = 'ḛ',
    f = 'ϝ',
    g = 'ḡ',
    h = 'ḥ',
    i = 'ḭ',
    j = 'ĵ',
    k = 'ḳ',
    l = 'ḽ',
    m = 'ṃ',
    n = 'ṇ',
    o = 'ṓ',
    p = 'ṗ',
    q = 'ʠ',
    r = 'ṛ',
    s = 'ṡ',
    t = 'ţ',
    u = 'ṵ',
    v = 'ṽ',
    w = 'ẁ',
    x = 'ẋ',
    y = 'ẏ',
    z = 'ẓ',
    A = 'À',
    B = 'β',
    C = 'Ḉ',
    D = 'Ḍ',
    E = 'Ḛ',
    F = 'Ḟ',
    G = 'Ḡ',
    H = 'Ḥ',
    I = 'Ḭ',
    J = 'Ĵ',
    K = 'Ḱ',
    L = 'Ḻ',
    M = 'Ṁ',
    N = 'Ṅ',
    O = 'Ṏ',
    P = 'Ṕ',
    Q = 'Ǫ',
    R = 'Ṛ',
    S = 'Ṣ',
    T = 'Ṫ',
    U = 'Ṳ',
    V = 'Ṿ',
    W = 'Ŵ',
    X = 'Ẋ',
    Y = 'Ŷ',
    Z = 'Ż',
}

class I18nextPseudo implements i18next.PostProcessorModule {
    options: I18nextPseudoOptions;
    name: string;
    type: 'postProcessor';

    constructor(options: I18nextPseudoOptions) {
        this.options = options;
        this.name = 'pseudo';
        this.type = 'postProcessor';
    }

    process(value: string, _key: string, _options: i18next.TOptions, _translator: any): string {
        if (!this.options.enabled) {
            return value;
        }
        const processedValue = value
            .split('')
            .map(character => {
                return (Object as any).keys(LargerLetters).includes(character) ?
                    LargerLetters[character as keyof typeof LargerLetters] : character;
            })
            .join('');
        return '[[ ' + processedValue + ' ]]';
    }
}

export enum Language {
    Dutch = 'nl',
    English = 'en',
    German = 'de',
    Pseudo = 'pseudo'
}

const defaultLanguage = Language.Dutch;

class I18nLogic {
    private _commonModule: LanguageModule | undefined;
    private _currentModule: LanguageModule | undefined;
    private _language: KnockoutObservable<string> = ko.observable("");
    private _i18next: i18next.i18n = i18next.createInstance();
    private initialization: Promise<void> | null = null;
    private _pseudo: I18nextPseudo = new I18nextPseudo({ enabled: false });

    public createLanguageModule(name: string, languageSources: LanguageSources): LanguageModule {
        return {
            name: name,
            fetchResource(language: string, done: ModuleFetchDone) {
                switch (language) {
                    case 'en':
                        languageSources.importEnglishLanguage().then(result => done(result));
                        break;
                    case 'de':
                        languageSources.importGermanLanguage().then(result => done(result));
                        break;
                    default:
                        console.error("Voor de taal '" + language + "' bestaat geen import in " + name);
                        break;
                }
            }
        }
    }

    public setLanguage(language: Language): Promise<void> | void {
        if (getBrowser().isIE()) {
            return;
        }
        return this.initialize().then(() => {
            this._i18next.changeLanguage(language, () => {
                this.setCookie(language);
                return this.downloadResourcesIfNeeded(language).then(() => this._language(language));
            });
        });
    }

    private setCookie(language: Language): void {
        if (areCookiesEnabled()) {
            setCookieValue("language", language, 30, false);
        }
    }

    public t(key: string | string[], options?: any): any {
        if (getBrowser().isIE()) {
            return;
        }
        this.initialize();
        return this._i18next.t(key, options);
    }

    public computed_t(key: string | string[], options?: any): KnockoutComputed<string> {
        return ko.computed(() => {
            this._language(); // to observe language changes
            return this.t(key, options);
        });
    }

    public changeModule(moduleDefaultResource: any, module: LanguageModule): void {
        if (getBrowser().isIE()) {
            return;
        }
        this.initialize();
        if (module.name === 'common') {
            this._commonModule = module;
        } else {
            this._currentModule = module;
        }
        this._i18next.addResourceBundle(defaultLanguage, module.name, moduleDefaultResource);
        this.initialize().then(() => this.downloadResourcesIfNeeded(ko.unwrap(this._language)));
    }

    public togglePseudo(): void {
        if (getBrowser().isIE()) {
            return;
        }
        this.initialize().then(() => {
            this._pseudo.options.enabled = !this._pseudo.options.enabled;
            this._language.valueHasMutated!();
        });
    }

    public isPseudoEnabled(): boolean {
        return this._pseudo.options.enabled;
    }

    private downloadResourcesIfNeeded(language: string): JQueryDeferred<any> {
        var mainDef = $.Deferred();
        var currentDef = $.Deferred();
        var commonDef = $.Deferred();

        if (language !== defaultLanguage) {
            if (this._commonModule) {
                this._commonModule.fetchResource(language, (resource: any) => { this._i18next.addResourceBundle(language, this._commonModule!.name, resource); commonDef.resolve(); });
            } else {
                commonDef.resolve();
            }
            if (this._currentModule) {
                this._currentModule.fetchResource(language, (resource: any) => { this._i18next.addResourceBundle(language, this._currentModule!.name, resource); currentDef.resolve(); });
            } else {
                currentDef.resolve();
            }
            $.when.apply($, [currentDef, commonDef]).then(() => mainDef.resolve());
        } else {
            mainDef.resolve();
        }

        return mainDef;
    }

    public initialize() {
        if (!this.initialization) {
            let language: Language;
            this._language = ko.observable("");
            const enablePseudo = getCookieValue("pseudo") === "true";
            this._pseudo = new I18nextPseudo({ enabled: enablePseudo });

            if (areCookiesEnabled() && (<any>Object).keys(Language).map((e: any) => Language[e]).includes(getCookieValue("language"))) {
                language = <Language>getCookieValue("language");
            } else {
                language = defaultLanguage;
            }

            this._i18next = i18next.createInstance();
            this.initialization = this._i18next
                .use(this._pseudo)
                .init({ debug: true, lng: language, fallbackLng: defaultLanguage, defaultNS: '', initImmediate: false, postProcess: ['pseudo'] })
                .then(() => {
                    this.setI18nBindingHandler();
                    this.setCookie(language);
                    return this.downloadResourcesIfNeeded(language).then(() => this._language(language));
                });
        }
        return this.initialization;
    }

    public resetInitialization() {
        this.initialization = null;
    }

    private setI18nBindingHandler(): void {
        /*
         * Verwijzingen naar vertalingen mogen 
         * 1. zich 'los' in een i18n data-bind bevinden: 
         *       i18n.common.button.opslaan
         * 2. zich in een object met een veld 'key' en een optioneel veld 'options' bevinden: 
         *       { key: i18n.werkbonnen.historie.bezoeken, options: { aantal: 5 } }
         * 3. zich in een object met een verwijzing naar een attribuut bevinden, in combinatie met een losse key: 
         *       { title: i18n.common.button.opslaan }
         * 4. zich in een object met een verwijzing naar een attribuut bevinden, in combinatie met een 'key' en 'options':
         *       { title: { key: i18n.werkbonnen.historie.bezoeken, options: { aantal: 5 } } }
         */
        ko.bindingHandlers.i18n = {
            update: (element, valueAccessor, _allBindingsAccessor, _viewModel, _bindingContext) => {
                this._language(); // op deze manier gaat de update functie af als de language observable wijzigt

                function getTranslationErrorTekst() {
                    console.error("Er is geen key gevonden voor de data-bind in het element: \n", element);
                    return "<Tekst kan niet worden bepaald>";
                }

                try {
                    var value = ko.toJS(valueAccessor());
                } catch (error) { // afvangen typfout in verwijzing in parent niveau, bijv i18n.cXmmon.button.opslaan
                    element.innerText = getTranslationErrorTekst();
                    return;
                }

                if (typeof value === 'undefined') { // afvangen typfout in verwijzing in diepste niveau, bijv i18n.common.button.Xpslaan
                    element.innerText = getTranslationErrorTekst();
                } else if (typeof value === 'string') {
                    element.innerText = this.t(value);
                } else if (typeof value.key === 'string') {
                    element.innerText = this.t(value.key, value.options);
                } else {
                    for (var propertyName in value) {
                        var propertyValue = value[propertyName];
                        var translation;
                        if (typeof propertyValue === 'undefined') { // afvangen typfout wanneer key zich in object bevindt, bijv { key: i18n.common.button.Xpslaan }
                            translation = getTranslationErrorTekst();
                        } else if (typeof propertyValue === 'string') {
                            translation = this.t(propertyValue);
                        } else if (typeof propertyValue.key === 'string') {
                            translation = this.t(propertyValue.key, propertyValue.options);
                        } else {
                            translation = getTranslationErrorTekst(); // afvangen edge cases
                        }
                        element.setAttribute(propertyName, translation);
                    }
                }
            }
        }
    }
}

export let i18n = (window as any).i18n = (window as any).i18n as I18nLogic || new I18nLogic();
